import React, { createContext, useContext, useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Workspace } from '../types';
import { confirmWorkspaceChange } from './workspaceDirtyRegistry';
import { WORKSPACE_SESSION_CHANGED_EVENT } from './workspaceSession';
import { setActiveRequestWorkspace } from '@/services/api/client';

// Canonical Men's First Team workspace — the seed default all legacy data is
// attached to. Used as the preferred default when a browser has no saved choice.
const MENS_WORKSPACE_ID = 'a0000000-0000-0000-0000-000000000001';

// Choose the default workspace when the user has no saved preference: prefer the
// Men's First Team, with graceful fallbacks if it isn't present.
function pickDefaultWorkspace(workspaces: Workspace[]): Workspace | null {
  if (workspaces.length === 0) return null;
  return (
    workspaces.find(w => w.id === MENS_WORKSPACE_ID) ||
    workspaces.find(w => w.shortName?.toLowerCase() === 'men') ||
    workspaces.find(w => /men'?s/i.test(w.name) && !/women/i.test(w.name)) ||
    workspaces[0]
  );
}

function storedWorkspaces(): Workspace[] {
  const saved = localStorage.getItem('workspaces');
  if (!saved) return [];
  const parsed: unknown = JSON.parse(saved);
  if (!Array.isArray(parsed) || !parsed.every(workspace => (
    typeof workspace === 'object' && workspace !== null
    && typeof workspace.id === 'string' && workspace.id.trim().length > 0
    && typeof workspace.name === 'string' && workspace.name.trim().length > 0
    && typeof workspace.shortName === 'string' && workspace.shortName.trim().length > 0
  ))) throw new Error('Invalid stored workspaces');
  return parsed as Workspace[];
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  availableWorkspaces: Workspace[];
  setCurrentWorkspace: (workspace: Workspace) => void;
  setAvailableWorkspaces: (workspaces: Workspace[]) => void;
  hasWyscout: boolean;
  isSwitchingWorkspace: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const transition = useRef(0);
  const [isSwitchingWorkspace, setIsSwitchingWorkspace] = useState(false);
  const [availableWorkspaces, setAvailableWorkspacesState] = useState<Workspace[]>(() => {
    try {
      return storedWorkspaces();
    } catch {
      return [];
    }
  });

  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(() => {
    try {
      const savedId = localStorage.getItem('currentWorkspaceId');
      const workspaces = storedWorkspaces();
      if (savedId) {
        // Honour the saved preference (persisted per-browser); fall back to default.
        return workspaces.find(w => w.id === savedId) || pickDefaultWorkspace(workspaces);
      }
      return pickDefaultWorkspace(workspaces);
    } catch {
      return null;
    }
  });

  const syncWorkspaceSession = useCallback(() => {
    ++transition.current;
    setIsSwitchingWorkspace(false);
    try {
      const workspaces = storedWorkspaces();
      const savedId = localStorage.getItem('currentWorkspaceId');
      const workspace = workspaces.find(item => item.id === savedId) || pickDefaultWorkspace(workspaces);

      setAvailableWorkspacesState(workspaces);
      setCurrentWorkspaceState(workspace);
      setActiveRequestWorkspace(workspace?.id ?? '');
      if (workspace) localStorage.setItem('currentWorkspaceId', workspace.id);
      else localStorage.removeItem('currentWorkspaceId');
    } catch {
      localStorage.removeItem('workspaces');
      localStorage.removeItem('currentWorkspaceId');
      setAvailableWorkspacesState([]);
      setCurrentWorkspaceState(null);
      setActiveRequestWorkspace('');
    }
  }, []);

  useLayoutEffect(() => {
    syncWorkspaceSession();
    window.addEventListener(WORKSPACE_SESSION_CHANGED_EVENT, syncWorkspaceSession);
    return () => window.removeEventListener(WORKSPACE_SESSION_CHANGED_EVENT, syncWorkspaceSession);
  }, [syncWorkspaceSession]);

  const setCurrentWorkspace = useCallback((workspace: Workspace) => {
    const oldWorkspaceId = currentWorkspace?.id ?? '';
    if (workspace.id === oldWorkspaceId || !confirmWorkspaceChange()) return;
    const generation = ++transition.current;
    setIsSwitchingWorkspace(true);
    setActiveRequestWorkspace('');
    void (async () => {
      if (oldWorkspaceId) {
        await queryClient.cancelQueries({ predicate: query => query.queryKey.includes(oldWorkspaceId) });
        if (generation !== transition.current) return;
        queryClient.removeQueries({ predicate: query => query.queryKey.includes(oldWorkspaceId) });
      }
      queryClient.removeQueries({ predicate: query => ['players', 'matches', 'fixtures', 'wyscout-matches', 'calendar', 'calendar-feed', 'calendar-groups', 'calendar-users'].includes(String(query.queryKey[0])) });
      if (generation !== transition.current) return;
      localStorage.setItem('currentWorkspaceId', workspace.id);
      setActiveRequestWorkspace(workspace.id);
      setCurrentWorkspaceState(workspace);
      setIsSwitchingWorkspace(false);
    })();
  }, [currentWorkspace?.id, queryClient]);

  useLayoutEffect(() => { setActiveRequestWorkspace(currentWorkspace?.id ?? ''); }, [currentWorkspace?.id]);

  useEffect(() => {
    const refreshCapabilities = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string; url?: string }>).detail;
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId || detail?.workspaceId !== workspaceId) return;
      const url = detail.url || '';
      const root = url.startsWith('/v2/reports') ? ['reports-v2', workspaceId]
        : url.startsWith('/v2/matches') ? ['matches-v2', workspaceId]
          : url.startsWith('/v2/calendar') ? ['calendar-v2', workspaceId]
            : url.startsWith('/v2/season') ? ['season-v2', workspaceId]
              : url.startsWith('/v2/league') ? ['v2', 'league', workspaceId]
                : url.startsWith('/v2/players') || url.startsWith('/v2/player-comparisons') || url.startsWith('/v2/team-selection') ? ['v2', 'players', workspaceId]
                  : null;
      if (root) void queryClient.invalidateQueries({ queryKey: root });
    };
    window.addEventListener('v2-forbidden', refreshCapabilities);
    return () => window.removeEventListener('v2-forbidden', refreshCapabilities);
  }, [currentWorkspace?.id, queryClient]);

  const setAvailableWorkspaces = useCallback((workspaces: Workspace[]) => {
    setAvailableWorkspacesState(workspaces);
    localStorage.setItem('workspaces', JSON.stringify(workspaces));
    if (workspaces.length > 0) {
      const currentId = localStorage.getItem('currentWorkspaceId');
      const found = workspaces.find(w => w.id === currentId);
      if (!found) {
        const def = pickDefaultWorkspace(workspaces);
        if (def) setCurrentWorkspace(def);
      }
    }
  }, [setCurrentWorkspace]);

  const hasWyscout = currentWorkspace?.wyscoutTeamId != null;

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        availableWorkspaces,
        setCurrentWorkspace,
        setAvailableWorkspaces,
        hasWyscout,
        isSwitchingWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};
